package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ClubSettingsViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.ClubPostDto
import com.arena_web.data.dto.ClubRuleDto
import com.arena_web.ui.components.widgets.MatchedText
import com.arena_web.ui.components.widgets.SuggestionMuted
import com.arena_web.utils.AddressPrediction
import com.arena_web.utils.GeoCoordinates
import com.arena_web.utils.getUserCoordinates
import com.arena_web.utils.autocompleteAddress
import com.arena_web.utils.cachedUserCoordinates
import com.arena_web.utils.resolveAddressPrediction
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.business.CreateClubContent
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.layouts.BusinessPageLayout
import com.arena_web.ui.layouts.LocalSelectedClubId
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

@Page("/settings")
@Composable
fun SettingsPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()
    val selectedClubId by container.selectedClubId.collectAsState()
    val clubAdminRepo = container.clubAdminRepository

    val viewModel = if (clubAdminRepo != null) rememberViewModel {
        ClubSettingsViewModel(clubAdminRepo)
    } else null

    val state = viewModel?.uiState?.collectAsState()?.value

    BusinessPageLayout(activeTab = BusinessSidebarTab.ClubSettings, showEmptyStateWhenNoClubs = false) {
        if (selectedClubId != null && viewModel != null && state != null) {
            val layoutClubId = LocalSelectedClubId.current
            LaunchedEffect(layoutClubId) {
                if (layoutClubId != null) {
                    if (layoutClubId != state.club?.id) {
                        viewModel.switchClub(layoutClubId)
                    } else {
                        viewModel.loadClub(layoutClubId)
                    }
                }
            }

            if (state.isLoading && state.club == null) return@BusinessPageLayout

            var showEditClubInfo by remember { mutableStateOf(false) }

            // Main layout: Left + Right
            Div(attrs = {
                style {
                    width(100.percent)
                    height(100.percent)
                    property("display", "flex")
                    property("gap", s(20))
                    property("padding", "${s(24)} ${s(40)}")
                    property("overflow", "auto")
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
            }) {
                // Left Container
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("min-width", "0")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", s(20))
                    }
                }) {
                    // Image Gallery Section
                    ClubSettingsImageGallery(
                        club = state.club,
                        onUploadBanner = { bytes, name, type -> viewModel.uploadBanner(bytes, name, type) },
                        onUploadGalleryImage = { bytes, name, type -> viewModel.uploadGalleryImage(bytes, name, type) },
                        onReplaceGalleryImage = { index, bytes, name, type -> viewModel.replaceGalleryImage(index, bytes, name, type) },
                        onDeleteGalleryImage = { viewModel.deleteGalleryImage(it) },
                        onEditClubInfo = { showEditClubInfo = true },
                        onDeleteClub = {
                            viewModel.deleteClub {
                                container.invalidateClubs()
                                scope.launch {
                                    container.ensureClubsLoaded()
                                }
                                ctx.router.navigateTo(Routes.DASHBOARD)
                            }
                        }
                    )

                    // Bottom Row: Rules/Services + Map + Reviews
                    Div(attrs = {
                        style {
                            property("display", "flex")
                            property("gap", s(20))
                            property("flex", "1")
                            property("min-height", "0")
                        }
                    }) {
                        // Left cards: Rules + Services
                        Div(attrs = {
                            style {
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("gap", s(20))
                                property("width", s(440))
                                property("flex-shrink", "0")
                                property("flex-grow", "0")
                            }
                        }) {
                            ClubSettingsCourtRules(
                                rules = state.club?.rules ?: emptyList(),
                                newRuleText = state.newRuleText,
                                onRuleTextChange = { viewModel.updateNewRuleText(it) },
                                onAddRule = { viewModel.addRule() }
                            )
                            ClubSettingsServicesCard(
                                amenities = state.amenities,
                                onUpdateAmenities = { viewModel.updateAmenities(it) }
                            )
                        }

                        // Map + Reviews
                        Div(attrs = {
                            style {
                                property("flex", "1")
                                property("min-width", "0")
                                property("display", "flex")
                                property("gap", s(20))
                            }
                        }) {
                            ClubSettingsMapSection(club = state.club)
                            ClubSettingsReviewsInfo(club = state.club)
                        }
                    }
                }

                // Right: Profile Card
                ClubSettingsProfileCard(
                    club = state.club,
                    posts = state.posts,
                    newPostText = state.newPostText,
                    postImageUrls = state.postImageUrls,
                    isUploadingPostImage = state.isUploadingPostImage,
                    isCreatingPost = state.isCreatingPost,
                    showComposePost = state.showComposePost,
                    onPostTextChange = { viewModel.updateNewPostText(it) },
                    onCreatePost = { viewModel.createPost() },
                    onShowCompose = { viewModel.showComposePost() },
                    onCancelCompose = { viewModel.hideComposePost() },
                    onUploadPostImage = { bytes, name, type -> viewModel.uploadPostImage(bytes, name, type) },
                    onRemovePostImage = { viewModel.removePostImage(it) },
                    onDeletePost = { viewModel.deletePost(it) }
                )
            }

            // Edit Club Info Dropdown
            if (showEditClubInfo) {
                EditClubInfoDropdown(
                    club = state.club,
                    onSave = { name, address, lat, lng, links ->
                        viewModel.updateClubInfo(
                            name, address, lat, lng,
                            instagramUrl = links.instagram,
                            websiteUrl = links.website,
                            googleSearchUrl = links.googleSearch,
                            googleMapsUrl = links.googleMaps,
                        )
                        showEditClubInfo = false
                    },
                    onUploadBanner = { bytes, name, type -> viewModel.uploadBanner(bytes, name, type) },
                    onClose = { showEditClubInfo = false }
                )
            }
        } else {
            CreateClubContent(
                onClubCreated = {
                    container.invalidateClubs()
                    scope.launch {
                        container.ensureClubsLoaded()
                    }
                    ctx.router.navigateTo(Routes.DASHBOARD)
                }
            )
        }
    }
}

// =============================================================================
// Image Gallery Section
// =============================================================================

@Composable
private fun ClubSettingsImageGallery(
    club: ClubDto?,
    onUploadBanner: (ByteArray, String, String) -> Unit,
    onUploadGalleryImage: (ByteArray, String, String) -> Unit,
    onReplaceGalleryImage: (Int, ByteArray, String, String) -> Unit,
    onDeleteGalleryImage: (Int) -> Unit,
    onEditClubInfo: () -> Unit,
    onDeleteClub: () -> Unit
) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            property("display", "flex")
            property("gap", s(12))
            property("height", s(342))
            property("flex-shrink", "0")
        }
    }) {
        // Main Banner — clickable to upload/replace
        Box(
            modifier = Modifier
                .sHeight(342)
                .sBorderRadius(22)
                .cursor(Cursor.Pointer)
                .onClick {
                    pickImageFile { bytes, name, type -> onUploadBanner(bytes, name, type) }
                }
                .styleModifier {
                    property("flex", "1.8")
                    property("min-width", s(400))
                    property("position", "relative")
                    property("overflow", "hidden")
                    property(
                        "background",
                        "linear-gradient(201deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                    )
                }
        ) {
            if (!club?.mainPhotoUrl.isNullOrBlank()) {
                Image(
                    src = club!!.mainPhotoUrl!!,
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("object-fit", "cover")
                    }
                )
            }

            // Venue Details Overlay
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("left", s(14))
                        property("top", s(14))
                        property("padding", "${s(18)} ${s(22)}")
                        property("border-radius", s(22))
                        property("background", "rgba(2, 4, 11, 0.4)")
                        property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                        property("backdrop-filter", "blur(11px)")
                    }
            ) {
                Row(modifier = Modifier.sGap(12), verticalAlignment = Alignment.Top) {
                    Column(modifier = Modifier.sGap(4).styleModifier { property("flex", "1") }) {
                        SpanText(
                            club?.name ?: "",
                            modifier = Modifier
                                .sFontSize(32)
                                .fontWeight(AppTypography.SemiBold)
                                .sLineHeight(38)
                                .sLetterSpacing(0.32)
                                .color(AppColors.Text.Primary)
                                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        )
                        Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                src = AppIcons.Location,
                                modifier = Modifier.sSize(24).styleModifier {
                                    property("filter", AppColors.IconDefaultFilter)
                                }
                            )
                            SpanText(
                                club?.address ?: "",
                                modifier = Modifier
                                    .bodyXlRegular()
                                    .color(AppColors.Text.Primary)
                            )
                        }
                    }
                    // 3-dots menu
                    var showClubMenu by remember { mutableStateOf(false) }
                    Box(
                        modifier = Modifier
                            .sSize(36)
                            .sBorderRadius(18)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                it.stopPropagation()
                                showClubMenu = !showClubMenu
                            }
                            .styleModifier {
                                property("background", "rgba(255, 255, 255, 0.15)")
                                property("backdrop-filter", "blur(8px)")
                                property("flex-shrink", "0")
                                property("position", "relative")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.EllipsisVertical,
                            modifier = Modifier.sSize(18).styleModifier {
                                property("filter", "brightness(0) invert(1)")
                            }
                        )

                        // Dropdown menu
                        if (showClubMenu) {
                            Box(
                                modifier = Modifier
                                    .styleModifier {
                                        property("position", "absolute")
                                        property("top", s(40))
                                        property("right", "0")
                                        property("z-index", "10")
                                        property("min-width", s(160))
                                        property("background", "rgba(15, 23, 52, 0.95)")
                                        property("border", "1px solid rgba(46, 96, 217, 0.4)")
                                        property("border-radius", s(12))
                                        property("backdrop-filter", "blur(20px)")
                                        property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.4)")
                                        property("overflow", "hidden")
                                    }
                            ) {
                                Column {
                                    // Edit option
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .sPadding(12, 16)
                                            .cursor(Cursor.Pointer)
                                            .onClick {
                                                it.stopPropagation()
                                                showClubMenu = false
                                                onEditClubInfo()
                                            }
                                            .styleModifier {
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("gap", s(10))
                                            }
                                    ) {
                                        Icon(
                                            src = AppIcons.Pencil,
                                            modifier = Modifier.sSize(18).styleModifier {
                                                property("filter", AppColors.IconDefaultFilter)
                                            }
                                        )
                                        SpanText(
                                            t.commonEdit,
                                            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                                        )
                                    }

                                    // Delete option
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .sPadding(12, 16)
                                            .cursor(Cursor.Pointer)
                                            .onClick {
                                                it.stopPropagation()
                                                showClubMenu = false
                                                onDeleteClub()
                                            }
                                            .styleModifier {
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("gap", s(10))
                                            }
                                    ) {
                                        Icon(
                                            src = AppIcons.Trash,
                                            modifier = Modifier.sSize(18).styleModifier {
                                                property("filter", "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(6000%) hue-rotate(348deg) brightness(95%)")
                                            }
                                        )
                                        SpanText(
                                            t.pageDeleteClub,
                                            modifier = Modifier.bodyLMedium().color(AppColors.Error)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Add Banner overlay when no image
            if (club?.mainPhotoUrl.isNullOrBlank()) {
                Column(
                    modifier = Modifier.fillMaxSize().sGap(10),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        src = AppIcons.Expand,
                        modifier = Modifier.sSize(48).styleModifier {
                            property("filter", AppColors.IconDefaultFilter)
                            property("opacity", "0.5")
                        }
                    )
                    SpanText(
                        t.pageAddBanner,
                        modifier = Modifier.bodyXlRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    )
                }
            }
        }

        // Right image grid: 1 tall + 2 small
        Div(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", s(12))
            }
        }) {
            val gallery = club?.gallery ?: emptyList()

            // Top tall slot
            GalleryPhotoSlot(
                index = 0,
                imageUrl = gallery.getOrNull(0)?.imageUrl,
                onUpload = onUploadGalleryImage,
                onReplace = onReplaceGalleryImage,
                onDelete = onDeleteGalleryImage,
                modifier = Modifier.fillMaxWidth().sHeight(165)
            )

            // Bottom row: 2 small
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(12))
                    property("flex", "1")
                    property("min-height", "0")
                }
            }) {
                GalleryPhotoSlot(
                    index = 1,
                    imageUrl = gallery.getOrNull(1)?.imageUrl,
                    onUpload = onUploadGalleryImage,
                    onReplace = onReplaceGalleryImage,
                    onDelete = onDeleteGalleryImage,
                    modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    }
                )
                GalleryPhotoSlot(
                    index = 2,
                    imageUrl = gallery.getOrNull(2)?.imageUrl,
                    onUpload = onUploadGalleryImage,
                    onReplace = onReplaceGalleryImage,
                    onDelete = onDeleteGalleryImage,
                    modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    }
                )
            }
        }
    }
}

@Composable
private fun GalleryPhotoSlot(
    index: Int,
    imageUrl: String?,
    onUpload: (ByteArray, String, String) -> Unit,
    onReplace: (Int, ByteArray, String, String) -> Unit,
    onDelete: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var isHovered by remember { mutableStateOf(false) }
    val hasImage = !imageUrl.isNullOrBlank()

    Box(
        modifier = modifier
            .sBorderRadius(22)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("overflow", "hidden")
                property("position", "relative")
                if (!hasImage) {
                    property("border", "2px dashed #0061FF")
                    property(
                        "background",
                        "linear-gradient(190deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                    )
                }
            }
            .onClick {
                if (!hasImage) {
                    pickImageFile { bytes, name, type -> onUpload(bytes, name, type) }
                }
            }
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false },
        contentAlignment = Alignment.Center
    ) {
        if (hasImage) {
            Image(
                src = imageUrl!!,
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("object-fit", "cover")
                }
            )

            // Hover overlay with replace & delete
            if (isHovered) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("background", "rgba(0, 0, 0, 0.5)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("gap", s(12))
                        }
                ) {
                    // Replace button
                    Box(
                        modifier = Modifier
                            .sSize(40)
                            .sBorderRadius(20)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                it.stopPropagation()
                                pickImageFile { bytes, name, type -> onReplace(index, bytes, name, type) }
                            }
                            .styleModifier {
                                property("background", "rgba(255, 255, 255, 0.15)")
                                property("backdrop-filter", "blur(8px)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.Pencil,
                            modifier = Modifier.sSize(20).styleModifier {
                                property("filter", "brightness(0) invert(1)")
                            }
                        )
                    }

                    // Delete button
                    Box(
                        modifier = Modifier
                            .sSize(40)
                            .sBorderRadius(20)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                it.stopPropagation()
                                onDelete(index)
                            }
                            .styleModifier {
                                property("background", "rgba(255, 60, 60, 0.3)")
                                property("backdrop-filter", "blur(8px)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.Trash,
                            modifier = Modifier.sSize(20).styleModifier {
                                property("filter", "brightness(0) invert(1)")
                            }
                        )
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier.sGap(10),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(
                    src = AppIcons.Add,
                    modifier = Modifier.sSize(48).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                        property("opacity", "0.5")
                    }
                )
                SpanText(
                    t.feedAddPhoto,
                    modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                )
            }
        }
    }
}

// =============================================================================
// Court Rules Card
// =============================================================================

@Composable
private fun ClubSettingsCourtRules(
    rules: List<ClubRuleDto>,
    newRuleText: String,
    onRuleTextChange: (String) -> Unit,
    onAddRule: () -> Unit
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(22)
            .styleModifier {
                property("flex", "1")
                property("min-height", "0")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("overflow", "hidden")
                property("position", "relative")
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(16, 18)
                .sGap(22)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.pageCourtRules,
                    modifier = Modifier
                        .h2Medium()
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("flex", "1") }
                )
                Icon(
                    src = AppIcons.Document,
                    modifier = Modifier.sSize(24).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                        property("cursor", "pointer")
                    }
                )
            }

            // Rules list
            rules.forEach { rule ->
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(10),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier.sSize(4).sBorderRadius(100)
                            .styleModifier {
                                property("background", "#0061FF")
                                property("flex-shrink", "0")
                            }
                    )
                    SpanText(
                        rule.text,
                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                    )
                }
            }

            // Input
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .sBorderRadius(28)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("position", "relative")
                    }
            ) {
                // Gradient border
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("border", "0.3px solid transparent")
                            property("border-radius", s(28))
                            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("-webkit-mask-composite", "xor")
                            property("mask-composite", "exclude")
                            property("pointer-events", "none")
                        }
                )
                Div(attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("display", "flex")
                        property("align-items", "center")
                        property("padding", "0 ${s(12)}")
                    }
                }) {
                    org.jetbrains.compose.web.dom.TextInput(value = newRuleText) {
                        onInput { onRuleTextChange(it.value) }
                        onKeyDown { event ->
                            if (event.key == "Enter" && newRuleText.isNotBlank()) {
                                event.preventDefault()
                                onAddRule()
                            }
                        }
                        style {
                            width(100.percent)
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("color", AppColors.Text.Primary.toString())
                            property("font-family", "'Urbanist', sans-serif")
                            property("font-size", s(16))
                        }
                        attr("placeholder", t.pageRulePlaceholder)
                    }
                }
            }

            // Add Rule button
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("align-self", "center")
                        property("flex-shrink", "0")
                        property("background", "#0061FF")
                        property("border-radius", s(22))
                        property("padding", "${s(13)} ${s(26)}")
                        property("cursor", "pointer")
                    }
                    .onClick { onAddRule() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.pageAddRule,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }

    }
}

// =============================================================================
// Services Card
// =============================================================================

private data class AmenityInfo(val type: String, val label: String, val icon: String)

private val ALL_AMENITIES = listOf(
    AmenityInfo("PARKING", "Parking", AppIcons.Parking),
    AmenityInfo("CAFE", "Cafe", AppIcons.Cafe),
    AmenityInfo("WIFI", "WiFi", AppIcons.Wifi),
    AmenityInfo("RECEPTION", "Reception", AppIcons.Reception),
    AmenityInfo("SHOWER", "Shower", AppIcons.Shower),
    AmenityInfo("FIRST_AID", "First aid", AppIcons.FirstAid),
)

@Composable
private fun ClubSettingsServicesCard(
    amenities: List<String>,
    onUpdateAmenities: (List<String>) -> Unit
) {
    val t = LocalAppStrings.current
    fun amenityLabel(info: AmenityInfo): String = when (info.type) {
        "PARKING" -> t.pageAmenityParking
        "CAFE" -> t.pageAmenityCafe
        "WIFI" -> t.pageAmenityWifi
        "RECEPTION" -> t.pageAmenityReception
        "SHOWER" -> t.pageAmenityShower
        "FIRST_AID" -> t.pageAmenityFirstAid
        else -> info.label
    }
    var showPopup by remember { mutableStateOf(false) }

    val enabledAmenities = ALL_AMENITIES.filter { it.type in amenities }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex", "1")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
            .sGap(8)
    ) {
        // 2-column grid of enabled amenities
        val rows = enabledAmenities.chunked(2)
        rows.forEach { row ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(8))
                    width(100.percent)
                }
            }) {
                row.forEach { info ->
                    ServiceChip(
                        name = amenityLabel(info),
                        icon = info.icon
                    )
                }
                if (row.size == 1) {
                    Box(modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    })
                }
            }
        }

        // "Edit Services" button — always blue at bottom
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("margin-top", "auto")
                },
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("background", "#0061FF")
                        property("border-radius", s(22))
                        property("padding", "${s(13)} ${s(26)}")
                        property("cursor", "pointer")
                    }
                    .onClick { showPopup = true },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.pageEditServices,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }

    // Amenities popup
    if (showPopup) {
        AmenitiesPopup(
            selectedAmenities = amenities,
            onSave = { onUpdateAmenities(it) },
            onClose = { showPopup = false }
        )
    }
}

@Composable
private fun ServiceChip(name: String, icon: String?) {
    Box(
        modifier = Modifier
            .sHeight(60)
            .sBorderRadius(14)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(199deg, rgba(60, 121, 241, 0.096) 34%, rgba(5, 4, 12, 0.09) 67%)"
                )
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Row(
            modifier = Modifier.sGap(8).sPaddingLeft(10),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) {
                Icon(
                    src = icon,
                    modifier = Modifier.sSize(24)
                )
            }
            SpanText(
                name,
                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
            )
        }
    }
}

@Composable
private fun AmenitiesPopup(
    selectedAmenities: List<String>,
    onSave: (List<String>) -> Unit,
    onClose: () -> Unit
) {
    val t = LocalAppStrings.current
    fun amenityLabel(info: AmenityInfo): String = when (info.type) {
        "PARKING" -> t.pageAmenityParking
        "CAFE" -> t.pageAmenityCafe
        "WIFI" -> t.pageAmenityWifi
        "RECEPTION" -> t.pageAmenityReception
        "SHOWER" -> t.pageAmenityShower
        "FIRST_AID" -> t.pageAmenityFirstAid
        else -> info.label
    }
    var localSelection by remember(selectedAmenities) { mutableStateOf(selectedAmenities.toSet()) }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onClose() }
            .animation(
                com.arena_web.ui.components.overlay.ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Card
        Box(
            modifier = Modifier
                .sWidth(400)
                .sBorderRadius(22)
                .sPadding(24)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("background", "#09142D")
                    property("border", "1px solid rgba(41, 52, 138, 0.3)")
                    property("box-shadow", "0 24px 48px rgba(0, 0, 0, 0.4)")
                }
                .animation(
                    com.arena_web.ui.components.overlay.ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        t.pageManageServices,
                        modifier = Modifier
                            .h2Medium()
                            .color(AppColors.Text.Secondary)
                            .styleModifier { property("flex", "1") }
                    )
                    Box(
                        modifier = Modifier
                            .sSize(32)
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { onClose() }
                            .styleModifier {
                                property("background", "rgba(255, 255, 255, 0.1)")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "\u00D7",
                            modifier = Modifier.sFontSize(18).color(AppColors.Text.Secondary)
                        )
                    }
                }

                // Amenities grid with selectable blocks
                Div(attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "1fr 1fr")
                        property("gap", s(10))
                        width(100.percent)
                    }
                }) {
                    ALL_AMENITIES.forEach { info ->
                        val isEnabled = info.type in localSelection

                        Box(
                            modifier = Modifier
                                .sHeight(60)
                                .sBorderRadius(14)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    localSelection = if (isEnabled) {
                                        localSelection - info.type
                                    } else {
                                        localSelection + info.type
                                    }
                                }
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("position", "relative")
                                    if (isEnabled) {
                                        property("border", "1px solid #0061FF")
                                        property(
                                            "background",
                                            "linear-gradient(199deg, rgba(0, 97, 255, 0.2) 34%, rgba(5, 4, 12, 0.15) 67%)"
                                        )
                                    } else {
                                        property("border", "1px solid rgba(41, 52, 138, 0.2)")
                                        property(
                                            "background",
                                            "linear-gradient(199deg, rgba(60, 121, 241, 0.05) 34%, rgba(5, 4, 12, 0.05) 67%)"
                                        )
                                        property("opacity", "0.5")
                                    }
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("transition", "all 0.2s ease")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Row(
                                modifier = Modifier.sGap(8),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    src = info.icon,
                                    modifier = Modifier.sSize(24)
                                )
                                SpanText(
                                    amenityLabel(info),
                                    modifier = Modifier.bodyLMedium().color(
                                        if (isEnabled) AppColors.Text.Primary else AppColors.Text.Muted
                                    )
                                )
                            }

                            // Check indicator for selected
                            if (isEnabled) {
                                Box(
                                    modifier = Modifier
                                        .sSize(18)
                                        .sBorderRadius(100)
                                        .styleModifier {
                                            property("position", "absolute")
                                            property("top", s(6))
                                            property("right", s(6))
                                            property("background", "#0061FF")
                                            property("display", "flex")
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(
                                        src = AppIcons.Check,
                                        modifier = Modifier.sSize(12).styleModifier {
                                            property("filter", "brightness(0) invert(1)")
                                        }
                                    )
                                }
                            }
                        }
                    }
                }

                // Save button
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(44)
                        .sBorderRadius(22)
                        .backgroundColor(AppColors.Primary)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            onSave(localSelection.toList())
                            onClose()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.commonSave,
                        modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }
        }
    }
}

// =============================================================================
// Map Section
// =============================================================================

@Composable
private fun ClubSettingsMapSection(club: ClubDto?) {
    val lat = club?.latitude ?: 0.0
    val lng = club?.longitude ?: 0.0

    Box(
        modifier = Modifier
            .sBorderRadius(22)
            .styleModifier {
                property("flex", "0.85")
                property("min-width", "0")
                property("height", "100%")
                property("overflow", "hidden")
                property("position", "relative")
            }
    ) {
        Div(attrs = {
            id("club-settings-map")
            style {
                width(100.percent)
                height(100.percent)
            }
        })

        LaunchedEffect(lat, lng) {
            delay(200)
            window.asDynamic().__clubSettingsMapLat = lat
            window.asDynamic().__clubSettingsMapLng = lng

            js("""
                (function() {
                    var id = 'club-settings-map';
                    var lat = window.__clubSettingsMapLat;
                    var lng = window.__clubSettingsMapLng;
                    var container = document.getElementById(id);
                    if (!container) return;
                    if (typeof L === 'undefined') return;

                    if (window.__clubSettingsMap) {
                        try { window.__clubSettingsMap.remove(); } catch(e) {}
                        window.__clubSettingsMap = null;
                    }

                    container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });
                    container.addEventListener('touchmove', function(e) { e.stopPropagation(); }, { passive: false });

                    var map = L.map(id, {
                        zoomControl: false,
                        attributionControl: false,
                        scrollWheelZoom: false,
                        dragging: false,
                        doubleClickZoom: false,
                        touchZoom: false,
                        boxZoom: false
                    }).setView([lat, lng], 15);

                    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                        maxZoom: 20,
                        subdomains: 'abcd'
                    }).addTo(map);

                    var markerIcon = L.divIcon({
                        className: '',
                        html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="26" height="35">' +
                            '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                            '<circle cx="12" cy="11" r="5" fill="white"/>' +
                            '</svg>',
                        iconSize: [26, 35],
                        iconAnchor: [13, 35]
                    });

                    L.marker([lat, lng], { icon: markerIcon }).addTo(map);

                    window.__clubSettingsMap = map;
                    setTimeout(function() { map.invalidateSize(); }, 150);
                })();
            """)
        }

        DisposableEffect(Unit) {
            onDispose {
                js("""
                    (function() {
                        if (window.__clubSettingsMap) {
                            try { window.__clubSettingsMap.remove(); } catch(e) {}
                            window.__clubSettingsMap = null;
                        }
                    })();
                """)
            }
        }

    }
}

// =============================================================================
// Reviews Info
// =============================================================================

@Composable
private fun ClubSettingsReviewsInfo(club: ClubDto?) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(22)
            .sPadding(16)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
            }
    ) {
        Column(modifier = Modifier.sGap(12)) {
            // Rating
            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "${club?.averageRating ?: "0.0"}",
                    modifier = Modifier.bodyXlRegular().color(AppColors.Text.Primary)
                )
                Icon(
                    src = AppIcons.StarRating16,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", "brightness(0) saturate(100%) invert(79%) sepia(70%) saturate(500%) hue-rotate(5deg)")
                    }
                )
                SpanText(
                    "(${club?.totalRatingsCount ?: 0})",
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                )
            }

            // Reviews / Avg Rating
            Row(modifier = Modifier.sGap(16)) {
                Row(modifier = Modifier.sGap(4)) {
                    SpanText(
                        t.pageReviewsLabel,
                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                    )
                    SpanText(
                        "${club?.totalRatingsCount ?: 0}",
                        modifier = Modifier.bodyXlRegular().color(AppColors.Text.Primary)
                    )
                }
                Row(modifier = Modifier.sGap(4)) {
                    SpanText(
                        t.pageAvgRatingLabel,
                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                    )
                    SpanText(
                        "${club?.averageRating ?: 0}",
                        modifier = Modifier.bodyXlRegular().color(AppColors.Text.Primary)
                    )
                }
            }

            // Note
            SpanText(
                t.pageReviewsPlaceholderNote,
                modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
            )
        }
    }
}

// =============================================================================
// Profile Card (Right Side)
// =============================================================================

@Composable
private fun ClubSettingsProfileCard(
    club: ClubDto?,
    posts: List<ClubPostDto>,
    newPostText: String,
    postImageUrls: List<String>,
    isUploadingPostImage: Boolean,
    isCreatingPost: Boolean,
    showComposePost: Boolean,
    onPostTextChange: (String) -> Unit,
    onCreatePost: () -> Unit,
    onShowCompose: () -> Unit,
    onCancelCompose: () -> Unit,
    onUploadPostImage: (ByteArray, String, String) -> Unit,
    onRemovePostImage: (Int) -> Unit,
    onDeletePost: (String) -> Unit
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sWidth(569)
            .styleModifier {
                property("flex-shrink", "0")
                property("height", "100%")
                property("border-radius", "${s(22)} ${s(22)} 0 0")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("backdrop-filter", "blur(11px)")
                property("overflow", "hidden")
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(22, 16)
                .sGap(18)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            if (showComposePost) {
                // Compose Post Card
                ComposePostCard(
                    postText = newPostText,
                    imageUrls = postImageUrls,
                    isUploading = isUploadingPostImage,
                    isCreating = isCreatingPost,
                    onTextChange = onPostTextChange,
                    onAddImage = onUploadPostImage,
                    onRemoveImage = onRemovePostImage,
                    onCancel = onCancelCompose,
                    onPost = onCreatePost
                )
            } else {
                // "What's happening at your club?" input
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(44)
                        .sBorderRadius(28)
                        .cursor(Cursor.Pointer)
                        .onClick { onShowCompose() }
                        .styleModifier {
                            property("flex-shrink", "0")
                            property("position", "relative")
                        }
                ) {
                    // Gradient border
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "0.3px solid transparent")
                                property("border-radius", s(28))
                                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                    )
                    Div(attrs = {
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("display", "flex")
                            property("align-items", "center")
                            property("padding", "0 ${s(12)}")
                            property("gap", s(12))
                        }
                    }) {
                        Icon(
                            src = AppIcons.Add,
                            modifier = Modifier.sSize(15).styleModifier {
                                property("filter", AppColors.IconDefaultFilter)
                                property("opacity", "0.5")
                            }
                        )
                        SpanText(
                            t.pageClubComposePrompt,
                            modifier = Modifier.bodyLMedium().color(AppColors.Text.Muted)
                        )
                    }
                }
            }

            // Posts or Empty State
            if (posts.isEmpty() && !showComposePost) {
                // Empty State
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("flex", "1")
                            property("justify-content", "center")
                        }
                        .sGap(24),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    TintedGlyph(src = AppIcons.Camera, sizePx = 114)
                    SpanText(
                        t.pageNoPostsYet,
                        modifier = Modifier.h2Medium().color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        t.pageClubNoPostsHint,
                        modifier = Modifier
                            .bodyLMedium()
                            .color(AppColors.Text.Secondary)
                            .textAlign(TextAlign.Center)
                    )
                }
            } else if (posts.isNotEmpty()) {
                // Posts Feed
                Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
                    posts.forEach { post ->
                        ClubPostCard(
                            club = club,
                            post = post,
                            onDeletePost = { onDeletePost(post.id) }
                        )
                    }
                }
            }
        }
    }
}

// =============================================================================
// Compose Post Card
// =============================================================================

@Composable
private fun ComposePostCard(
    postText: String,
    imageUrls: List<String>,
    isUploading: Boolean,
    isCreating: Boolean,
    onTextChange: (String) -> Unit,
    onAddImage: (ByteArray, String, String) -> Unit,
    onRemoveImage: (Int) -> Unit,
    onCancel: () -> Unit,
    onPost: () -> Unit
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .sPadding(20, 18)
            .sGap(16)
            .styleModifier {
                property("flex-shrink", "0")
                property("background", "rgba(9, 26, 64, 0.6)")
                property("border", "1px solid rgba(46, 96, 217, 0.3)")
            }
    ) {
        // Title
        SpanText(
            t.pageComposePostTitle,
            modifier = Modifier.h3().color(AppColors.Text.Primary)
        )

        // Text area
        Div(attrs = {
            style {
                width(100.percent)
                property("position", "relative")
            }
        }) {
            org.jetbrains.compose.web.dom.TextArea(value = postText) {
                onInput { onTextChange(it.value) }
                style {
                    width(100.percent)
                    property("min-height", s(100))
                    property("background", "rgba(255, 255, 255, 0.03)")
                    property("border", "1px solid rgba(46, 96, 217, 0.2)")
                    property("border-radius", s(12))
                    property("padding", s(14))
                    property("color", AppColors.Text.Primary.toString())
                    property("font-family", "'Urbanist', sans-serif")
                    property("font-size", s(14))
                    property("line-height", "1.5")
                    property("resize", "vertical")
                    property("outline", "none")
                }
                attr("placeholder", t.pagePostContentPlaceholder)
            }
        }

        // Image upload slots
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12)
        ) {
            // Show uploaded images + empty slots (max 2)
            for (i in 0 until 2) {
                if (i < imageUrls.size) {
                    // Uploaded image preview
                    Box(
                        modifier = Modifier
                            .sSize(80)
                            .sBorderRadius(12)
                            .styleModifier {
                                property("position", "relative")
                                property("overflow", "hidden")
                                property("flex-shrink", "0")
                            }
                    ) {
                        Image(
                            src = imageUrls[i],
                            modifier = Modifier.fillMaxSize().styleModifier {
                                property("object-fit", "cover")
                            }
                        )
                        // Remove button
                        Box(
                            modifier = Modifier
                                .sSize(20)
                                .sBorderRadius(10)
                                .cursor(Cursor.Pointer)
                                .onClick { onRemoveImage(i) }
                                .styleModifier {
                                    property("position", "absolute")
                                    property("top", s(4))
                                    property("right", s(4))
                                    property("background", "rgba(0, 0, 0, 0.6)")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                src = AppIcons.Close,
                                modifier = Modifier.sSize(10).styleModifier {
                                    property("filter", "brightness(0) invert(1)")
                                }
                            )
                        }
                    }
                } else if (i == imageUrls.size) {
                    // Empty upload slot
                    val slotIndex = i
                    Box(
                        modifier = Modifier
                            .sSize(80)
                            .sBorderRadius(12)
                            .cursor(if (isUploading) Cursor.Default else Cursor.Pointer)
                            .onClick {
                                if (!isUploading) {
                                    pickImageFile { bytes, name, type ->
                                        onAddImage(bytes, name, type)
                                    }
                                }
                            }
                            .styleModifier {
                                property("flex-shrink", "0")
                                property("background", "rgba(9, 26, 64, 0.5)")
                                property("border", "1.5px dashed ${AppColors.Primary}")
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("gap", s(6))
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        if (isUploading) {
                            SpanText(
                                "...",
                                modifier = Modifier.bodyLMedium().color(AppColors.Primary)
                            )
                        } else {
                            Icon(
                                src = AppIcons.Add,
                                modifier = Modifier.sSize(20).styleModifier {
                                    property("filter", "brightness(0) saturate(100%) invert(26%) sepia(93%) saturate(7471%) hue-rotate(217deg) brightness(102%) contrast(105%)")
                                }
                            )
                            SpanText(
                                t.pageAddImageSlot(slotIndex + 1),
                                modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }
        }

        // Buttons row
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            horizontalArrangement = Arrangement.End
        ) {
            // Cancel button
            Box(
                modifier = Modifier
                    .sHeight(40)
                    .sBorderRadius(20)
                    .sPadding(0, 20)
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() }
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("border", "1px solid rgba(255, 255, 255, 0.15)")
                        property("background", "transparent")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.commonCancel,
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
            }

            // Post button
            Box(
                modifier = Modifier
                    .sHeight(40)
                    .sBorderRadius(20)
                    .sPadding(0, 28)
                    .cursor(if (postText.isBlank() || isCreating) Cursor.Default else Cursor.Pointer)
                    .onClick { if (postText.isNotBlank() && !isCreating) onPost() }
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("background", if (postText.isBlank() || isCreating) "rgba(0, 97, 255, 0.4)" else AppColors.Primary.toString())
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (isCreating) t.pagePosting else t.pagePostAction,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }
}

// =============================================================================
// Post Card
// =============================================================================

@Composable
private fun ClubPostCard(club: ClubDto?, post: ClubPostDto, onDeletePost: () -> Unit) {
    val t = LocalAppStrings.current
    var showMenu by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .sPadding(16, 12)
            .sGap(12)
    ) {
        // Header: Avatar + Name + Date + 3-dots menu
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar
            Box(
                modifier = Modifier
                    .sSize(40)
                    .sBorderRadius(20)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                        property("background", "rgba(60, 121, 241, 0.2)")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (!club?.mainPhotoUrl.isNullOrBlank()) {
                    Image(
                        src = club!!.mainPhotoUrl!!,
                        modifier = Modifier.fillMaxSize().styleModifier {
                            property("object-fit", "cover")
                        }
                    )
                } else {
                    Icon(
                        src = AppIcons.Profile,
                        modifier = Modifier.sSize(20).styleModifier {
                            property("filter", AppColors.IconDefaultFilter)
                        }
                    )
                }
            }

            // Name + Date
            Column(
                modifier = Modifier.styleModifier {
                    property("flex", "1")
                    property("min-width", "0")
                }
                    .sGap(4)
            ) {
                SpanText(
                    club?.name ?: "",
                    modifier = Modifier.bodyXlMedium().color(AppColors.Text.Primary)
                )
                SpanText(
                    formatDateTime(post.createdAt),
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Subtle)
                )
            }

            // 3-dots menu
            Box(
                modifier = Modifier
                    .sSize(44)
                    .cursor(Cursor.Pointer)
                    .onClick { showMenu = !showMenu }
                    .styleModifier { property("position", "relative") },
                contentAlignment = Alignment.CenterEnd
            ) {
                Icon(
                    src = AppIcons.EllipsisVertical,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )

                // Dropdown menu
                if (showMenu) {
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("top", s(40))
                                property("right", "0")
                                property("z-index", "10")
                                property("min-width", s(140))
                                property("background", "rgba(15, 23, 52, 0.95)")
                                property("border", "1px solid rgba(46, 96, 217, 0.4)")
                                property("border-radius", s(12))
                                property("backdrop-filter", "blur(20px)")
                                property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.4)")
                                property("overflow", "hidden")
                            }
                    ) {
                        // Delete option
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sPadding(12, 16)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    showMenu = false
                                    onDeletePost()
                                }
                                .styleModifier {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", s(10))
                                }
                        ) {
                            Icon(
                                src = AppIcons.Trash,
                                modifier = Modifier.sSize(18).styleModifier {
                                    property("filter", "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(6000%) hue-rotate(348deg) brightness(95%)")
                                }
                            )
                            SpanText(
                                t.commonDelete,
                                modifier = Modifier.bodyLMedium().color(AppColors.Error)
                            )
                        }
                    }
                }
            }
        }

        // Post content
        if (post.content.isNotBlank()) {
            SpanText(
                post.content,
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
        }

        // Post images
        if (post.images.isNotEmpty()) {
            PostImageGrid(images = post.images.map { it.imageUrl })
        }

        // Likes
        Row(modifier = Modifier.sGap(8).styleModifier { property("padding-left", s(2)) }, verticalAlignment = Alignment.CenterVertically) {
            Icon(
                src = AppIcons.Heart,
                modifier = Modifier.sSize(24).styleModifier {
                    if (post.isLikedByMe) {
                        property("filter", "brightness(0) saturate(100%) invert(24%) sepia(95%) saturate(6000%) hue-rotate(348deg)")
                    } else {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                }
            )
            SpanText(
                "${post.likesCount}",
                modifier = Modifier
                    .styleModifier {
                        property("font-family", "'Urbanist', sans-serif")
                        property("font-weight", "400")
                        property("font-size", s(18))
                        property("line-height", s(18))
                        property("letter-spacing", "${s(0.5)}")
                    }
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

@Composable
private fun PostImageGrid(images: List<String>) {
    when {
        images.size == 1 -> {
            Image(
                src = images[0],
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(265)
                    .sBorderRadius(12)
                    .styleModifier { property("object-fit", "cover") }
            )
        }

        images.size == 2 -> {
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(5))
                    property("height", s(265))
                    property("width", "100%")
                }
            }) {
                images.forEach { url ->
                    Image(
                        src = url,
                        modifier = Modifier
                            .styleModifier {
                                property("flex", "1")
                                property("min-width", "0")
                                property("height", "100%")
                                property("object-fit", "cover")
                                property("border-radius", s(12))
                            }
                    )
                }
            }
        }

        images.size >= 3 -> {
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(5))
                    property("height", s(265))
                    property("width", "100%")
                }
            }) {
                // Left large image
                Image(
                    src = images[0],
                    modifier = Modifier
                        .styleModifier {
                            property("flex", "1")
                            property("min-width", "0")
                            property("height", "100%")
                            property("object-fit", "cover")
                            property("border-radius", s(12))
                        }
                )
                // Right column with 2 images
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("min-width", "0")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", s(5))
                    }
                }) {
                    images.drop(1).take(2).forEach { url ->
                        Image(
                            src = url,
                            modifier = Modifier
                                .styleModifier {
                                    property("flex", "1")
                                    property("min-height", "0")
                                    property("width", "100%")
                                    property("object-fit", "cover")
                                    property("border-radius", s(12))
                                }
                        )
                    }
                }
            }
        }
    }
}

// =============================================================================
// Edit Club Info Dropdown
// =============================================================================

@Composable
private fun EditClubInfoDropdown(
    club: ClubDto?,
    onSave: (name: String, address: String, lat: Double?, lng: Double?, links: ClubLinks) -> Unit,
    onUploadBanner: (ByteArray, String, String) -> Unit,
    onClose: () -> Unit
) {
    val t = LocalAppStrings.current
    var editName by remember(club?.name) { mutableStateOf(club?.name ?: "") }

    // External links — blank clears the link on save.
    var instagram by remember(club?.instagramUrl) { mutableStateOf(club?.instagramUrl ?: "") }
    var website by remember(club?.websiteUrl) { mutableStateOf(club?.websiteUrl ?: "") }
    var googleSearch by remember(club?.googleSearchUrl) { mutableStateOf(club?.googleSearchUrl ?: "") }
    var googleMaps by remember(club?.googleMapsUrl) { mutableStateOf(club?.googleMapsUrl ?: "") }

    // Address autocomplete state
    var address by remember(club?.address) { mutableStateOf(club?.address ?: "") }
    var addressQuery by remember(club?.address) { mutableStateOf(club?.address ?: "") }
    var latitude by remember { mutableStateOf<Double?>(null) }
    var longitude by remember { mutableStateOf<Double?>(null) }
    var addressSuggestions by remember { mutableStateOf<List<AddressPrediction>>(emptyList()) }
    var showSuggestions by remember { mutableStateOf(false) }
    // Seeded from the session geo cache (warmed at page load) — first
    // keystroke is already biased; the fetch below may upgrade IP to GPS.
    var userCoords by remember { mutableStateOf(cachedUserCoordinates()) }
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    // Get user geolocation once for biased address search
    LaunchedEffect(Unit) {
        userCoords = getUserCoordinates()
    }

    // Debounced address search
    LaunchedEffect(addressQuery) {
        if (addressQuery.length < 3 || address.isNotBlank()) {
            addressSuggestions = emptyList()
            return@LaunchedEffect
        }
        delay(300)
        addressSuggestions = autocompleteAddress(
            placesApi = container.placesApi,
            query = addressQuery,
            biasLat = userCoords?.latitude,
            biasLng = userCoords?.longitude,
        )
        showSuggestions = addressSuggestions.isNotEmpty()
    }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onClose() }
            .animation(
                com.arena_web.ui.components.overlay.ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Card
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sMaxWidth(520)
                .sBorderRadius(18)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("background", "#09142D")
                    property("box-shadow", "0 24px 48px rgba(0, 0, 0, 0.4)")
                    property("padding", "${s(18)} ${s(22)}")
                    property("margin", "0 ${s(20)}")
                }
                .animation(
                    com.arena_web.ui.components.overlay.ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            Column(modifier = Modifier.fillMaxWidth().sGap(20)) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        t.pageEditClubInfo,
                        modifier = Modifier
                            .sFontSize(20)
                            .fontWeight(AppTypography.Medium)
                            .color(AppColors.Text.Secondary)
                            .styleModifier { property("flex", "1") }
                    )
                    Box(
                        modifier = Modifier
                            .sSize(32)
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { onClose() }
                            .styleModifier {
                                property("background", "rgba(255, 255, 255, 0.1)")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "\u00D7",
                            modifier = Modifier.sFontSize(18).color(AppColors.Text.Secondary)
                        )
                    }
                }

                // Banner preview
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(100)
                        .sBorderRadius(12)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            pickImageFile { bytes, name, type -> onUploadBanner(bytes, name, type) }
                        }
                        .styleModifier {
                            property("overflow", "hidden")
                            property("position", "relative")
                            if (club?.mainPhotoUrl.isNullOrBlank()) {
                                property(
                                    "background",
                                    "linear-gradient(201deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                                )
                                property("border", "1.5px dashed ${AppColors.Primary}")
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    if (!club?.mainPhotoUrl.isNullOrBlank()) {
                        Image(
                            src = club!!.mainPhotoUrl!!,
                            modifier = Modifier.fillMaxSize().styleModifier {
                                property("object-fit", "cover")
                            }
                        )
                        // Hover overlay hint
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .styleModifier {
                                    property("position", "absolute")
                                    property("inset", "0")
                                    property("background", "rgba(0, 0, 0, 0.3)")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("opacity", "0")
                                    property("transition", "opacity 0.2s ease")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                src = AppIcons.Pencil,
                                modifier = Modifier.sSize(24).styleModifier {
                                    property("filter", "brightness(0) invert(1)")
                                }
                            )
                        }
                    } else {
                        Column(
                            modifier = Modifier.sGap(6),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(
                                src = AppIcons.Add,
                                modifier = Modifier.sSize(28).styleModifier {
                                    property("filter", AppColors.IconDefaultFilter)
                                    property("opacity", "0.5")
                                }
                            )
                            SpanText(
                                t.pageAddBanner,
                                modifier = Modifier.bodySRegular().color(BusinessColors.Text.OnSurfaceMuted)
                            )
                        }
                    }
                }

                // Club name field
                Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                    SpanText(
                        t.pageClubNameLabel,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(44)
                            .sBorderRadius(28)
                            .styleModifier {
                                property("position", "relative")
                            }
                    ) {
                        // Gradient border
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .styleModifier {
                                    property("position", "absolute")
                                    property("inset", "0")
                                    property("border", "0.3px solid transparent")
                                    property("border-radius", s(28))
                                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                    property("-webkit-mask-composite", "xor")
                                    property("mask-composite", "exclude")
                                    property("pointer-events", "none")
                                }
                        )
                        Div(attrs = {
                            style {
                                width(100.percent)
                                height(100.percent)
                                property("display", "flex")
                                property("align-items", "center")
                                property("padding", "0 ${s(16)}")
                            }
                        }) {
                            org.jetbrains.compose.web.dom.TextInput(value = editName) {
                                onInput { editName = it.value }
                                style {
                                    width(100.percent)
                                    property("background", "transparent")
                                    property("border", "none")
                                    property("outline", "none")
                                    property("color", AppColors.Text.Primary.toString())
                                    property("font-family", "'Urbanist', sans-serif")
                                    property("font-size", s(16))
                                }
                                attr("placeholder", t.pageClubNameLabel)
                            }
                        }
                    }
                }

                // Address field with autocomplete
                Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                    SpanText(
                        t.pageAddressLabel,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )

                    // Unified expanding surface (PersonalInfoEditModal city
                    // pattern): the outer Box reserves the 44px slot; the inner
                    // Box is absolute and grows downward with the suggestions
                    // inside the same rounded border, floating OVER the content.
                    if (showSuggestions && addressSuggestions.isNotEmpty()) {
                        Box(
                            modifier = Modifier
                                .position(Position.Fixed)
                                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                                .zIndex(99)
                                .onClick { showSuggestions = false }
                        )
                    }
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(44)
                            .styleModifier { property("position", "relative") }
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sMinHeight(44)
                                .sBorderRadius(28)
                                .position(Position.Absolute)
                                .zIndex(if (showSuggestions && addressSuggestions.isNotEmpty()) 100 else 10)
                                .styleModifier {
                                    property("top", "0")
                                    property("left", "0")
                                    property(
                                        "background",
                                        "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                            "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box"
                                    )
                                }
                        ) {
                            // Gradient border ring (masked) — the surface's only chrome.
                            Box(
                                modifier = Modifier
                                    .styleModifier {
                                        property("position", "absolute")
                                        property("inset", "0")
                                        property("border", "0.3px solid transparent")
                                        property("border-radius", s(28))
                                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                        property("-webkit-mask-composite", "xor")
                                        property("mask-composite", "exclude")
                                        property("pointer-events", "none")
                                    }
                            )
                            Column(modifier = Modifier.fillMaxWidth()) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .sHeight(44)
                                        .styleModifier {
                                            property("padding-left", s(12))
                                            property("padding-right", s(8))
                                            property("gap", s(12))
                                            property("box-sizing", "border-box")
                                        },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(
                                        src = AppIcons.Location,
                                        modifier = Modifier.sSize(18).styleModifier {
                                            property("opacity", "0.5")
                                            property("flex-shrink", "0")
                                        }
                                    )
                                    Input(type = InputType.Text) {
                                        value(addressQuery)
                                        onInput {
                                            val newValue = it.value
                                            addressQuery = newValue
                                            if (address.isNotBlank() && newValue != address) {
                                                address = ""
                                                latitude = null
                                                longitude = null
                                            }
                                        }
                                        attr("placeholder", t.pageSearchAddressPlaceholder)
                                        attr("autocomplete", "off")

                                        style {
                                            width(100.percent)
                                            property("background", "transparent")
                                            property("border", "none")
                                            property("outline", "none")
                                            property("font-family", "Urbanist, sans-serif")
                                            property("font-size", s(16))
                                            property("font-weight", "300")
                                            property("line-height", "1.5")
                                            property("letter-spacing", s(0.032))
                                            property("color", AppColors.Text.Secondary.toString())
                                        }
                                    }
                                }

                                if (showSuggestions && addressSuggestions.isNotEmpty()) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .sHeight(1)
                                            .sMarginBottom(6)
                                            .styleModifier {
                                                property("background", "rgba(255, 255, 255, 0.06)")
                                            }
                                    )
                                    Column(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .styleModifier {
                                                property("padding", "0 ${s(6)} ${s(6)}")
                                                property("max-height", "240px")
                                                property("overflow-y", "auto")
                                                property("scrollbar-width", "thin")
                                                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                            }
                                    ) {
                                        addressSuggestions.forEach { prediction ->
                                            Column(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .sPadding(8, 6)
                                                    .sBorderRadius(8)
                                                    .cursor(Cursor.Pointer)
                                                    .onClick {
                                                        // Google predictions carry no
                                                        // coordinates - one details call
                                                        // resolves them (and closes the
                                                        // billing session).
                                                        showSuggestions = false
                                                        addressQuery = prediction.title
                                                        scope.launch {
                                                            val result =
                                                                resolveAddressPrediction(container.placesApi, prediction)
                                                                    ?: return@launch
                                                            address = listOfNotNull(
                                                                result.street,
                                                                result.houseNumber,
                                                                result.city,
                                                                result.country
                                                            ).filter { it.isNotBlank() }.joinToString(", ")
                                                            addressQuery = result.title
                                                            latitude = result.lat
                                                            longitude = result.lng
                                                        }
                                                    }
                                            ) {
                                                MatchedText(prediction.title, addressQuery.trim())
                                                if (prediction.subtitle.isNotBlank()) {
                                                    SpanText(
                                                        prediction.subtitle,
                                                        modifier = Modifier
                                                            .sFontSize(12)
                                                            .color(SuggestionMuted)
                                                            .styleModifier {
                                                                property("overflow", "hidden")
                                                                property("text-overflow", "ellipsis")
                                                                property("white-space", "nowrap")
                                                                property("margin-top", "2px")
                                                            }
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // External links — brand names, deliberately not localized.
                ClubLinkField(label = "Instagram", value = instagram, onChange = { instagram = it })
                ClubLinkField(label = "Website", value = website, onChange = { website = it })
                ClubLinkField(label = "Google Search", value = googleSearch, onChange = { googleSearch = it })
                ClubLinkField(label = "Google Maps", value = googleMaps, onChange = { googleMaps = it })

                // Save button
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(44)
                        .sBorderRadius(22)
                        .backgroundColor(AppColors.Primary)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            if (editName.isNotBlank()) {
                                onSave(
                                    editName.trim(),
                                    address.ifBlank { addressQuery.trim() },
                                    latitude,
                                    longitude,
                                    ClubLinks(
                                        instagram = instagram.trim(),
                                        website = website.trim(),
                                        googleSearch = googleSearch.trim(),
                                        googleMaps = googleMaps.trim(),
                                    )
                                )
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.pageSaveChanges,
                        modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }
        }
    }
}

/** External-link values collected by [EditClubInfoDropdown] — blank clears. */
private data class ClubLinks(
    val instagram: String,
    val website: String,
    val googleSearch: String,
    val googleMaps: String,
)

/** Labeled URL input with the same gradient-ring chrome as the name field. */
@Composable
private fun ClubLinkField(
    label: String,
    value: String,
    onChange: (String) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(44)
                .sBorderRadius(28)
                .styleModifier {
                    property("position", "relative")
                }
        ) {
            // Gradient border
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(28))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )
            Div(attrs = {
                style {
                    width(100.percent)
                    height(100.percent)
                    property("display", "flex")
                    property("align-items", "center")
                    property("padding", "0 ${s(16)}")
                }
            }) {
                org.jetbrains.compose.web.dom.TextInput(value = value) {
                    onInput { onChange(it.value) }
                    style {
                        width(100.percent)
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("color", AppColors.Text.Primary.toString())
                        property("font-family", "'Urbanist', sans-serif")
                        property("font-size", s(16))
                    }
                    attr("placeholder", "https://…")
                }
            }
        }
    }
}

// =============================================================================
// Helpers
// =============================================================================

private const val MAX_IMAGE_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB

private fun pickImageFile(onFilePicked: (ByteArray, String, String) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*"
    input.onchange = { _ ->
        val file = input.files?.get(0)
        if (file != null) {
            if (file.size.toInt() > MAX_IMAGE_SIZE_BYTES) {
                window.alert("Image is too large. Maximum size is 50 MB.")
            } else {
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    val bytes = int8Array.unsafeCast<ByteArray>()
                    onFilePicked(bytes, file.name, file.type)
                }
                reader.readAsArrayBuffer(file)
            }
        }
    }
    input.click()
}
